import { Button } from '../common/Button';
import { GlassPanel } from '../common/GlassPanel';
import { Input } from '../common/Input';
import { useAuthStore } from '../../stores/authStore';
import { useHeadsetDevices } from '../../hooks/useHeadsetDevices';
import type { HeadsetDevice } from '../../types/device';
import type { PairingState } from './headsetPairingState';
import { usePairingActions, usePairingHeader } from './headsetPairingState';

/**
 * Settings panel for pairing Quest headsets.
 */
export function HeadsetPairingPanel() {
  const { user } = useAuthStore();
  const devices = useHeadsetDevices(user?.uid);
  const { state, actions } = usePairingActions();
  const header = usePairingHeader(devices.length);

  return (
    <GlassPanel className="space-y-5">
      <PairingHeader header={header} />
      <PairingForm
        code={state.code}
        deviceName={state.deviceName}
        isBusy={state.status === 'loading'}
        onChangeCode={actions.setCode}
        onChangeDeviceName={actions.setDeviceName}
        onPair={actions.handlePair}
      />
      <PairingMessage status={state.status} message={state.message} />
      <DeviceList devices={devices} onUnpair={actions.handleUnpair} isBusy={state.status === 'loading'} />
    </GlassPanel>
  );
}

interface PairingHeaderProps {
  header: string;
}

/**
 * Renders the pairing panel header copy.
 */
function PairingHeader({ header }: PairingHeaderProps) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <h2 className="text-lg font-semibold text-white">Headset Pairing</h2>
        <p className="text-sm text-ink-200">
          Open VR Meet on your Quest, then enter the 4-character code shown in the headset.
        </p>
      </div>
      <div className="text-sm font-medium text-ink-200">{header}</div>
    </div>
  );
}

interface PairingFormProps {
  code: string;
  deviceName: string;
  isBusy: boolean;
  onChangeCode: (value: string) => void;
  onChangeDeviceName: (value: string) => void;
  onPair: () => void;
}

/**
 * Renders the pairing code form.
 */
function PairingForm({
  code,
  deviceName,
  isBusy,
  onChangeCode,
  onChangeDeviceName,
  onPair,
}: PairingFormProps) {
  return (
    <div className="grid md:grid-cols-[1fr_1fr_auto] gap-4 items-end">
      <Input
        label="Pairing code"
        value={code}
        onChange={(event) => onChangeCode(event.target.value.toUpperCase())}
        placeholder="AB12"
        maxLength={4}
      />
      <Input
        label="Device name"
        value={deviceName}
        onChange={(event) => onChangeDeviceName(event.target.value)}
        placeholder="Quest Headset"
        maxLength={40}
      />
      <Button variant="primary" size="md" onClick={onPair} disabled={isBusy}>
        Pair headset
      </Button>
    </div>
  );
}

interface PairingMessageProps {
  status: PairingState['status'];
  message: string;
}

/**
 * Renders pairing feedback message.
 */
function PairingMessage({ status, message }: PairingMessageProps) {
  if (!message) {
    return null;
  }

  const className = status === 'error' ? 'text-sm text-red-400' : 'text-sm text-emerald-300';
  return <div className={className}>{message}</div>;
}

interface DeviceListProps {
  devices: HeadsetDevice[];
  onUnpair: (deviceId: string) => void;
  isBusy: boolean;
}

/**
 * Renders a list of paired devices.
 */
function DeviceList({ devices, onUnpair, isBusy }: DeviceListProps) {
  if (devices.length === 0) {
    return (
      <div className="text-sm text-ink-200">
        No headsets paired yet. You can still join rooms with a room code in VR.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {devices.map((device) => (
        <div
          key={device.deviceId}
          className="flex items-center justify-between rounded-xl border border-white/10 px-4 py-3"
        >
          <div>
            <div className="text-white font-medium">{device.deviceName}</div>
            <div className="text-xs text-ink-200">
              Paired {device.pairedAt.toLocaleDateString()}
            </div>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onUnpair(device.deviceId)}
            disabled={isBusy}
          >
            Unpair
          </Button>
        </div>
      ))}
    </div>
  );
}
